import React from 'react';
import { HelpCircle } from 'lucide-react';
import { mockData } from '../mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const FAQSection = () => {
  const { faqs } = mockData;
  const [headerRef, headerVisible] = useScrollAnimation();
  const [accordionRef, accordionVisible] = useScrollAnimation();

  return (
    <section id="faq" className="relative py-12 md:py-24 bg-gradient-to-b from-gray-900 to-black">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,231,171,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,231,171,0.02)_1px,transparent_1px)] bg-[size:64px_64px]"></div>
      
      {/* Glowing Orbs */}
      <div className="absolute top-1/4 left-0 w-64 h-64 bg-[#FFE7AB]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-[#5F9BC7]/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`scroll-animate fade-in-bottom ${headerVisible ? 'visible' : ''} text-center mb-16`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFE7AB]/10 border border-[#FFE7AB]/30 backdrop-blur-sm mb-4">
            <HelpCircle className="w-4 h-4 text-[#FFE7AB]" />
            <span className="text-sm font-medium text-[#FFE7AB]">Common Questions</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Got questions? We've got answers. Here's everything you need to know.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div 
          ref={accordionRef}
          className={`scroll-animate fade-in-bottom ${accordionVisible ? 'visible' : ''} max-w-3xl mx-auto`}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/50 transition-all duration-300"
              >
                <AccordionTrigger className="px-6 py-5 text-left hover:no-underline group">
                  <span className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors duration-300">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5">
                  <div className="pt-2 border-t border-white/10">
                    <p className="text-gray-300 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Still Have Questions CTA */}
        <div className="text-center mt-16">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Still have questions?
          </h3>
          <p className="text-lg md:text-xl text-gray-300 mb-3 max-w-2xl mx-auto">
            Fill out the application form and we'll answer any questions you have during our consultation call.
          </p>
          <button
            onClick={() => document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' })}
            className="text-[#5F9BC7] text-lg font-semibold hover:text-[#4A8AB0] transition-colors duration-300 underline underline-offset-4 decoration-2"
          >
            Apply for Coaching →
          </button>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
